import React, { useState, useRef, useEffect } from "react";
import { signIn, completeNewPassword, invokeAgent, getToken } from "./AgentService";

const styles = {
  app: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    maxWidth: 800, margin: "0 auto", height: "100vh",
    display: "flex", flexDirection: "column", background: "#f8f9fa",
  },
  header: {
    background: "#0078d4", color: "#fff", padding: "16px 24px",
    display: "flex", alignItems: "center", gap: 12,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  headerTitle: { fontSize: 18, fontWeight: 600, margin: 0 },
  headerSub: { fontSize: 12, opacity: 0.85, margin: 0 },
  messages: {
    flex: 1, overflowY: "auto", padding: "24px 16px",
    display: "flex", flexDirection: "column", gap: 12,
  },
  bubble: (isUser) => ({
    maxWidth: "75%", padding: "12px 16px",
    borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
    background: isUser ? "#0078d4" : "#fff",
    color: isUser ? "#fff" : "#1a1a1a",
    alignSelf: isUser ? "flex-end" : "flex-start",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5,
  }),
  inputRow: {
    display: "flex", gap: 8, padding: "12px 16px",
    borderTop: "1px solid #e0e0e0", background: "#fff",
  },
  input: {
    flex: 1, padding: "10px 14px", borderRadius: 8,
    border: "1px solid #d0d0d0", fontSize: 14, outline: "none",
  },
  sendBtn: {
    padding: "10px 20px", borderRadius: 8, border: "none",
    background: "#0078d4", color: "#fff", fontWeight: 600,
    cursor: "pointer", fontSize: 14,
  },
  sendBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  loginContainer: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", flex: 1, gap: 12, padding: 32,
  },
  loginInput: {
    padding: "10px 14px", borderRadius: 8,
    border: "1px solid #d0d0d0", fontSize: 14, width: 280,
  },
  loginBtn: {
    padding: "10px 24px", borderRadius: 8, border: "none",
    background: "#0078d4", color: "#fff", fontWeight: 600,
    cursor: "pointer", fontSize: 14, width: 310,
  },
  error: { color: "#d32f2f", fontSize: 13, textAlign: "center" },
  typing: {
    alignSelf: "flex-start", padding: "8px 16px",
    color: "#888", fontSize: 13, fontStyle: "italic",
  },
};

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [needsNewPw, setNeedsNewPw] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError(""); setLoading(true);
    try {
      const result = await signIn(username, password);
      if (result?.newPasswordRequired) setNeedsNewPw(result);
      else onLogin();
    } catch (e) { setError(e.message || "Login failed"); }
    setLoading(false);
  };

  const handleNewPw = async () => {
    setError(""); setLoading(true);
    try {
      await completeNewPassword(needsNewPw.user, newPassword, needsNewPw.userAttributes);
      onLogin();
    } catch (e) { setError(e.message || "Password change failed"); }
    setLoading(false);
  };

  if (needsNewPw) {
    return (
      <div style={styles.loginContainer}>
        <h2 style={{ margin: 0, color: "#333" }}>Set new password</h2>
        <p style={{ color: "#666", fontSize: 13 }}>Your temporary password must be changed.</p>
        <input style={styles.loginInput} type="password" placeholder="New password"
          value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleNewPw()} />
        {error && <p style={styles.error}>{error}</p>}
        <button style={styles.loginBtn} onClick={handleNewPw} disabled={loading}>
          {loading ? "Updating..." : "Set password"}
        </button>
      </div>
    );
  }

  return (
    <div style={styles.loginContainer}>
      <h2 style={{ margin: 0, color: "#333" }}>ADO Backlog Agent</h2>
      <p style={{ color: "#666", fontSize: 13 }}>Sign in with your team credentials</p>
      <input style={styles.loginInput} type="text" placeholder="Email"
        value={username} onChange={(e) => setUsername(e.target.value)} />
      <input style={styles.loginInput} type="password" placeholder="Password"
        value={password} onChange={(e) => setPassword(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
      {error && <p style={styles.error}>{error}</p>}
      <button style={styles.loginBtn} onClick={handleLogin} disabled={loading}>
        {loading ? "Signing in..." : "Sign in"}
      </button>
    </div>
  );
}

function ChatScreen() {
  const [messages, setMessages] = useState([{
    role: "agent",
    text: "Hello! I'm your Azure DevOps backlog assistant. I can query, create, and update work items using natural language.\n\nTry:\n- \"Show me all active bugs in the current sprint\"\n- \"Create a user story for implementing dark mode\"\n- \"Move task 4521 to Active and assign to Sarah\"",
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);
    try {
      const response = await invokeAgent(text);
      setMessages((prev) => [...prev, { role: "agent", text: response }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "agent", text: `Error: ${e.message}` }]);
    }
    setLoading(false);
  };

  return (
    <>
      <div style={styles.messages}>
        {messages.map((m, i) => (
          <div key={i} style={styles.bubble(m.role === "user")}>{m.text}</div>
        ))}
        {loading && <div style={styles.typing}>Agent is thinking...</div>}
        <div ref={bottomRef} />
      </div>
      <div style={styles.inputRow}>
        <input style={styles.input} value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about your backlog..." disabled={loading} />
        <button style={{ ...styles.sendBtn, ...(loading ? styles.sendBtnDisabled : {}) }}
          onClick={send} disabled={loading}>Send</button>
      </div>
    </>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(!!getToken());
  return (
    <div style={styles.app}>
      <div style={styles.header}>
        <div>
          <p style={styles.headerTitle}>ADO Backlog Agent</p>
          <p style={styles.headerSub}>Natural language backlog management</p>
        </div>
      </div>
      {authed ? <ChatScreen /> : <LoginScreen onLogin={() => setAuthed(true)} />}
    </div>
  );
}
